/**
 * @ Author: Housefolios
 * @ Create Time: 2025-12-01 00:00:00
 * @ Modified by: David Helmick
 * @ Modified time: 2025-12-04 18:21:56
 * @ Description: Skip trace data display
 */

import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Grid, Button,  Typography } from '@mui/material'
import { get, map } from '@/utils/lodash'
import {
  alignSkipTraceResults,
  reformatSkipTraceData} from './alignSkipTraceResults'

const flattenObject = (obj, prefix = '', result = {}) => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key
      const value = obj[key]

      if (value === null || value === undefined) {
        result[newKey] = ''
      } else if (Array.isArray(value)) {
        // Handle arrays by creating indexed columns
        if (value.length === 0) {
          result[newKey] = ''
        } else {
          value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              flattenObject(item, `${newKey}.${index}`, result)
            } else {
              result[`${newKey}.${index}`] = item
            }
          })
        }
      } else if (typeof value === 'object') {
        flattenObject(value, newKey, result)
      } else {
        result[newKey] = value
      }
    }
  }
  return result
}

const convertToCSV = (data) => {
  const flattened = flattenObject(alignSkipTraceResults(data) || {})
  const headers = Object.keys(flattened).sort()
  const values = headers.map((header) => {
    const value = flattened[header]
    // Escape quotes and wrap in quotes if contains comma, newline, or quote
    if (value === null || value === undefined) {
      return ''
    }
    const stringValue = String(value)
    if (
      stringValue.includes(',') ||
      stringValue.includes('\n') ||
      stringValue.includes('"')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue
  })

  return [headers.join(','), values.join(',')].join('\n')
}

/**
 * Skip Trace Display
 *
 * @param {Object} skipTraceResults
 */
function SkipTraceDisplay(skipTraceResults) {
  return (
    <>
      {map(reformatSkipTraceData(skipTraceResults), (result, index) => (
        <Grid size={12} key={index}>
          <Typography
            variant="subtitle1"
            style={{
              fontWeight: 700,
              color: '#3f51b5',
              marginBottom: 8,
              marginTop: index > 0 ? 16 : 0}}
          >
            Contact #{index + 1}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Name:</span>{' '}
                {get(result, 'fullName') || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Phone:</span>{' '}
                {get(result, 'phone.phoneDisplay') ? (
                  <>
                    {get(result, 'phone.phoneDisplay')} ({' '}
                    {get(result, 'phone.phoneType')} )
                  </>
                ) : (
                  'N/A'
                )}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Email:</span>{' '}
                {get(result, 'email.email') || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>
                  Current Address:
                </span>{' '}
                {get(result, 'address.formattedAddress') || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Do Not Call:</span>{' '}
                {get(result, 'phone') ? (
                  <>{get(result, 'phone.doNotCall') ? 'True' : 'False'}</>
                ) : (
                  'N/A'
                )}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Date of Birth:</span>{' '}
                {get(result, 'dob') || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Gender:</span>{' '}
                {get(result, 'gender') || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Age:</span>{' '}
                {get(result, 'age') || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6, md: 4}}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Occupation:</span>{' '}
                {get(result, 'job.display') || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      ))}
      <Grid size={12}
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 20}}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const csvString = convertToCSV(skipTraceResults)
            const dataUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csvString)}`
            const link = document.createElement('a')
            link.href = dataUrl
            link.download = 'skip_trace_data.csv'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }}
        >
          <FontAwesomeIcon
            icon={['fal', 'cloud-download']}
            style={{ marginRight: 8 }}
          />
          Download Skip Trace Data
        </Button>
      </Grid>
    </>
  )
}

export default SkipTraceDisplay
export { alignSkipTraceResults, reformatSkipTraceData }
